import { useEffect, useState } from 'react';
import { useStaticGeneration } from '../../../shared/contexts/StaticGenerationContext';

import { getLoginErrorDescriptor } from '../services/authService';
import { LOGIN_STORAGE_KEY } from '../../../config/loginPageConfig';

const ALLOWED_FIELD_NAMES = new Set([
  'userId',
  'password',
  'rememberId',
]);

function readRememberedUserId() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return (
      window.localStorage.getItem(LOGIN_STORAGE_KEY) || ''
    );
  } catch {
    return '';
  }
}

function updateRememberedUserId(
  userId,
  shouldRemember,
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (shouldRemember) {
      window.localStorage.setItem(
        LOGIN_STORAGE_KEY,
        userId,
      );

      return;
    }

    window.localStorage.removeItem(LOGIN_STORAGE_KEY);
  } catch {
    // 아이디 저장 실패가 로그인 실패로 이어지지 않게 합니다.
  }
}

function createInitialForm() {
  const rememberedUserId = readRememberedUserId();

  return {
    userId: rememberedUserId,
    password: '',
    rememberId: Boolean(rememberedUserId),
  };
}

function validateLoginForm({ userId, password }) {
  if (!userId.trim()) {
    return {
      key: 'errors.userIdRequired',
      field: 'userId',
    };
  }

  if (userId.trim().length > 100) {
    return {
      key: 'errors.userIdTooLong',
      field: 'userId',
    };
  }

  if (!password) {
    return {
      key: 'errors.passwordRequired',
      field: 'password',
    };
  }

  if (password.length > 128) {
    return {
      key: 'errors.passwordTooLong',
      field: 'password',
    };
  }

  return null;
}

export default function useLoginForm({
  loginAction,
  onSuccess,
} = {}) {
  const isStatic = useStaticGeneration();
  const [restoreRemembered] = useState(isStatic);
  const [form, setForm] = useState(() => isStatic
    ? { userId: '', password: '', rememberId: false }
    : createInitialForm());
  useEffect(() => {
    if (restoreRemembered) setForm(createInitialForm());
  }, [restoreRemembered]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (event) => {
    const {
      name,
      type,
      checked,
      value,
    } = event.target;

    if (!ALLOWED_FIELD_NAMES.has(name)) {
      return;
    }

    setForm((previousForm) => ({
      ...previousForm,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));

    if (error) {
      setError(null);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(
      (previousValue) => !previousValue,
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (typeof loginAction !== 'function') {
      setError({
        key: 'errors.unknown',
        field: null,
      });

      return;
    }

    const normalizedForm = {
      ...form,
      userId: form.userId.trim(),
    };

    const validationError =
      validateLoginForm(normalizedForm);

    if (validationError) {
      setError(validationError);
      return;
    }

    setForm(normalizedForm);
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await loginAction({
        userId: normalizedForm.userId,
        password: normalizedForm.password,
      });

      updateRememberedUserId(
        normalizedForm.userId,
        normalizedForm.rememberId,
      );

      if (typeof onSuccess === 'function') {
        await onSuccess(response);
      }
    } catch (loginError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '[Login] 로그인 요청 실패:',
          loginError,
        );
      }

      setError(
        getLoginErrorDescriptor(loginError),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    showPassword,
    error,
    isSubmitting,
    handleInputChange,
    handleSubmit,
    togglePasswordVisibility,
  };
}